'use client'
import React, { useState, useRef } from 'react';
import dynamic from 'next/dynamic';
import { convertToNoCookieUrl } from '@/lib/videoUtils';

const ReactPlayer = dynamic(() => import('react-player'), { 
  ssr: false,
  loading: () => <div style={{ width: '100%', height: '200px', backgroundColor: '#f0f0f0' }} />
});
import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import Lightbox from './Lightbox';
import CustomIconButton from './CustomIconButton';
import PauseIcon from '@mui/icons-material/Pause';

interface VideoPlayerProps {
  src: string;
  autoplay?: boolean;
  muted?: boolean;
  loop?: boolean;
  controls?: boolean;
  aspectRatio?: string;
  className?: string;
  poster?: string;
}

const VideoPlayer: React.FC<VideoPlayerProps> = ({
  src,
  autoplay = false,
  muted = true,
  loop = false,
  controls = true,
  aspectRatio = '16/9',
  className,
  poster = null
}) => {
  const theme = useTheme();
  const playerRef = useRef<any>(null);
  const noCookieUrl = convertToNoCookieUrl(src);
  const [playing, setPlaying] = useState(autoplay);
  const [volume] = useState(0.8);
  const [internalMuted, setInternalMuted] = useState(muted);
  const [fullscreen, setFullscreen] = useState(false);
  const [preview, setPreview] = useState(poster || null);

  const handlePlayPause = () => {
    setPlaying(prev => !prev);
    setPreview(null);
  };

  const handleVolumeToggle = () => {
    setInternalMuted(prev => !prev);
  };

  const handleFullscreen = () => {
    setPlaying(false);
    setFullscreen(true);
  };

  const handleCloseFullscreen = () => {
    setFullscreen(false);
  };

  const handleClickPreview = () => {
    setPlaying(true);
    setPreview(null);
  };

  const audioControlStyles = {
    position: 'absolute',
    top: 12,
    left: 12,
    cursor: 'pointer',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: 32,
    height: 32,
    color: 'common.white',
    zIndex: 10,
    '& > svg': {
      fontSize: 32,
    },
    '&:hover': {
      opacity: 0.8,
    },
  };

  const fullscreenControlStyles = {
    position: 'absolute',
    top: 12,
    right: 12,
    cursor: 'pointer',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: 32,
    height: 32,
    color: 'common.white',
    zIndex: 10,
    '& > svg': {
      fontSize: 32,
    },
    '&:hover': {
      opacity: 0.8,
    },
  };

  const playPauseControlStyles = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    cursor: 'pointer',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: 64,
    height: 64,
    borderRadius: '50%',
    color: 'common.white',
    zIndex: 10,
    '& > svg': {
      fontSize: 48,
      width: 48,
      height: 48,
    },
    '&:hover': {
      opacity: 0.8,
      backgroundColor: 'transparent',
    },
  }

  const playerContainer = {
    position: 'relative',
    aspectRatio: aspectRatio,
    width: 'auto',
    overflow: 'hidden',
    '&:hover .video-controls': {
      opacity: 1
    },
    '& .video-controls': {
      opacity: playing ? 1 : 0,
      transition: 'opacity 0.3s ease-in-out'
    },
    '& .video-controls.play-button': {
      opacity: playing ? 0 : 1,
    },
    '&:hover .video-controls.play-button': {
      opacity: 1,
    }
  };

  return (
    <>
      <Box sx={playerContainer} className={className}>
        <ReactPlayer
          ref={playerRef}
          src={noCookieUrl}
          playing={playing}
          muted={internalMuted}
          volume={volume}
          loop={loop}
          width="auto"
          height="100%"
          controls={false}
          light={preview}
          playsInline
          playIcon={<></>}
          onClickPreview={handleClickPreview}
          config={{
            youtube: {
              rel: 0,
              fs: 0,
              iv_load_policy: 3,
              disablekb: 1,
            },
            vimeo: {
              title: false,
              byline: false,
              portrait: false,
              autopause: false,
              background: false
            }
          }}
        />

        {controls && (
          <>
            <Box 
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: 64,
                background: 'linear-gradient(180deg, rgba(0, 0, 0, 0.20) 0%, rgba(0, 0, 0, 0.00) 100%)',
                zIndex: 2,
                pointerEvents: 'none',
                opacity: playing ? 1 : 0,
                transition: 'opacity 0.3s ease-in-out'
              }}
              className="video-controls"
            />
            <Box 
              className="video-controls" 
              sx={{...audioControlStyles, zIndex: 10}} 
              onClick={(e) => { e.stopPropagation(); handleVolumeToggle(); }}
            >
              {internalMuted ? <VolumeOffIcon /> : <VolumeUpIcon />}
            </Box>
            
            <Box 
              className="video-controls" 
              sx={{...fullscreenControlStyles, zIndex: 10}} 
              onClick={(e) => { e.stopPropagation(); handleFullscreen(); }}
            >
              <FullscreenIcon />
            </Box>

            <CustomIconButton
              className="video-controls play-button"
              sx={{ ...playPauseControlStyles, zIndex: 12, opacity: 0 }}
              onClick={(e) => { e.stopPropagation(); handlePlayPause(); }}
            >
              {!playing ? (<PlayArrowIcon />) : <PauseIcon />}
            </CustomIconButton>
            
          </>
        )}
      </Box>

      <Lightbox
        open={fullscreen}
        onClose={handleCloseFullscreen}
      >
        <ReactPlayer
          src={noCookieUrl}
          playing={true}
          loop={loop}
          width="auto"
          height="100%"
          controls={true}
          // style={{ width: '100%', height: '100%' }}
          config={{
            youtube: {
              rel: 0,
              fs: 0,
              iv_load_policy: 3,
              disablekb: 0
            },
            vimeo: {
              title: false,
              byline: false,
              portrait: false,
              autopause: false,
              background: false
            }
          }}
        />
      </Lightbox>
    </>
  );
};

export default VideoPlayer;